import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabaseClient';

export const formatFareLabel = (fare) =>
  `${fare.name} - ${fare.seats} Seater (₹${fare.rate_per_km}/km)`;

const FALLBACK_FARES = [
  { slug: 'sedan', name: 'Sedan', seats: '4+1', rate_per_km: 11, show_in_booking: true, calculator_key: 'sedan', route_tier: 'sedan' },
  { slug: 'ertiga', name: 'Ertiga', seats: '6+1', rate_per_km: 14, show_in_booking: true, route_tier: 'suv' },
  { slug: 'innova_crysta', name: 'Innova Crysta', seats: '6+1', rate_per_km: 20, show_in_booking: true },
  { slug: 'suv', name: 'SUV', seats: '6+1', rate_per_km: 18, show_in_booking: true, calculator_key: 'suv' },
  { slug: 'tempo_traveller', name: 'Tempo Traveller', seats: '12+1', rate_per_km: 22, show_in_booking: true, calculator_key: 'tempo_traveller' },
  { slug: 'bus', name: 'Bus', seats: '35+', rate_per_km: 35, show_in_booking: false, calculator_key: 'bus' },
];

/** fleet_cabs.fare_slug → cab_fares.slug (no FK); join rates client-side. */
function joinFleetCabs(cabs, fares) {
  const rateBySlug = Object.fromEntries(fares.map((f) => [f.slug, f.rate_per_km]));
  return cabs.map((cab) => ({
    ...cab,
    rate_per_km: rateBySlug[cab.fare_slug] ?? 0,
  }));
}

export function useCabRates() {
  const [fares, setFares] = useState(FALLBACK_FARES);
  const [fleetCabs, setFleetCabs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [faresRes, fleetRes] = await Promise.all([
          supabase
            .from('cab_fares')
            .select('*')
            .eq('is_active', true)
            .order('sort_order')
            .order('name'),
          supabase
            .from('fleet_cabs')
            .select('*')
            .eq('is_active', true)
            .order('sort_order')
            .order('name'),
        ]);

        if (faresRes.error) throw faresRes.error;
        if (fleetRes.error) throw fleetRes.error;

        if (!cancelled) {
          const fareList = faresRes.data?.length ? faresRes.data : FALLBACK_FARES;
          if (faresRes.data?.length) setFares(faresRes.data);
          setFleetCabs(joinFleetCabs(fleetRes.data || [], fareList));
        }
      } catch {
        /* keep fallback fares */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const bookingOptions = useMemo(
    () =>
      fares
        .filter((f) => f.show_in_booking)
        .map((f) => ({
          value: formatFareLabel(f),
          label: formatFareLabel(f),
          slug: f.slug,
          rate: f.rate_per_km,
        })),
    [fares],
  );

  const rateBySlug = useMemo(() => {
    const map = {};
    fares.forEach((f) => {
      map[f.slug] = f.rate_per_km;
    });
    return map;
  }, [fares]);

  const rateByRouteTier = useMemo(() => {
    const map = {};
    fares.forEach((f) => {
      if (f.route_tier) map[f.route_tier] = f.rate_per_km;
    });
    return map;
  }, [fares]);

  const startingFare = useMemo(() => {
    const sedan = fares.find((f) => f.slug === 'sedan');
    return sedan?.rate_per_km ?? 11;
  }, [fares]);

  const vehicleOptions = useMemo(
    () =>
      fares
        .filter((f) => f.calculator_key && f.slug === f.calculator_key)
        .map((f) => ({
          id: f.calculator_key,
          label: f.name,
          seats: f.seats,
          rate_per_km: f.rate_per_km,
        })),
    [fares],
  );

  return {
    fares,
    fleetCabs,
    loading,
    bookingOptions,
    rateBySlug,
    rateByRouteTier,
    startingFare,
    vehicleOptions,
  };
}
